import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Pixels of vertical space rows do not get — a toolbar, a header row, a footer.
 * Re-run on every pass, so it can look elements up rather than hold them.
 * Return `null`/`undefined` for "nothing there yet".
 */
export type AutoFitMeasure = () => number | null | undefined;

export interface UseAutoFitRowsOptions {
  /**
   * Height of one row. A function rather than a value because the answer
   * changes: rows differ in height, and averaging the rendered ones is the only
   * reading that tracks the real mix. Constants and refs are one-liners —
   * `() => 40`, `() => ref.current?.getBoundingClientRect().height ?? null`.
   */
  rowHeight: AutoFitMeasure;
  /** Used until `rowHeight` yields a usable measurement. Default `48`. */
  fallbackRowHeight?: number;
  /** Everything inside the container that rows do not get. */
  reserve?: AutoFitMeasure[];
  /** Breathing room left below the last row. Default `0`. */
  bottomGap?: number;
  /** Never return fewer than this. Default `1`. */
  minRows?: number;
  /** Never return more than this. Default `Infinity`. */
  maxRows?: number;
  /**
   * Round the count *down* to a multiple of this. Default `1` (exact fit).
   * Raise it when each distinct count costs something — a server round-trip, a
   * cache entry — to trade up to `step - 1` rows of space for far fewer
   * distinct values. Rounding down rather than up is what keeps the last row
   * above the fold.
   */
  step?: number;
  /**
   * Extra elements whose resizing should trigger a re-measure, resolved once the
   * container has mounted. The container and the fold are watched already; this
   * is for content that can change height without moving either — a list body
   * inside a `max-height` box, whose rows re-wrap while the box stays put.
   */
  observe?: () => (Element | null)[];
}

export interface UseAutoFitRowsResult<TElement extends HTMLElement = HTMLElement> {
  /** Attach to the element whose top edge rows start from. */
  containerRef: RefObject<TElement>;
  /** How many rows fit, clamped to `minRows`/`maxRows`. `0` before first measure. */
  rows: number;
  /**
   * Whole pixels left for rows once the boundary, reserves and gap are taken
   * out. `0` before the first measure. Rounded, so sub-pixel layout jitter does
   * not re-render everything that depends on it.
   */
  availableHeight: number;
}

const CLIPPING_OVERFLOW = /^(auto|scroll|overlay|hidden|clip)$/;

/** The lowest y a row can occupy and still be seen: the nearest clipping edge. */
function findClippingAncestors(element: HTMLElement): HTMLElement[] {
  const ancestors: HTMLElement[] = [];
  let node: HTMLElement | null = element.parentElement;

  while (node && node !== document.body && node !== document.documentElement) {
    if (CLIPPING_OVERFLOW.test(window.getComputedStyle(node).overflowY)) ancestors.push(node);
    node = node.parentElement;
  }

  return ancestors;
}

function findFoldY(element: HTMLElement): number {
  return findClippingAncestors(element).reduce(
    (foldY: number, ancestor: HTMLElement): number =>
      Math.min(foldY, ancestor.getBoundingClientRect().bottom),
    window.innerHeight
  );
}

function sum(measures: AutoFitMeasure[]): number {
  return measures.reduce((total: number, measure: AutoFitMeasure) => total + (measure() ?? 0), 0);
}

/**
 * Reports how many rows of a list fit between a container's top edge and the
 * fold, and keeps that number current as the window, the container or the
 * surrounding chrome resizes.
 *
 * Deliberately knows nothing about tables — it measures DOM boxes, so it drives
 * a TanStack page size, an MUI `DataGrid`, a virtualiser's overscan or a plain
 * `<ul>` equally well.
 *
 * ```tsx
 * const { containerRef, rows } = useAutoFitRows<HTMLDivElement>({
 *   rowHeight: () => averageRenderedRowHeight(containerRef.current),
 *   reserve: [() => headerHeight(containerRef.current)],
 *   minRows: 5,
 * });
 * ```
 *
 * The result can feed back into itself: the row count decides which rows render,
 * and their mean height decides the row count. A reserve that re-wraps at
 * certain widths does the same through the header. Convergence therefore is not
 * free — see the damper in `measure`.
 */
export function useAutoFitRows<TElement extends HTMLElement = HTMLElement>({
  rowHeight,
  fallbackRowHeight = 48,
  reserve,
  bottomGap = 0,
  minRows = 1,
  maxRows = Number.POSITIVE_INFINITY,
  step = 1,
  observe,
}: UseAutoFitRowsOptions): UseAutoFitRowsResult<TElement> {
  const containerRef = useRef<TElement>(null);
  const [rows, setRows] = useState<number>(0);
  const [availableHeight, setAvailableHeight] = useState<number>(0);

  // Read through a ref so a caller passing fresh array/function literals every
  // render does not re-subscribe the observers below.
  const optionsRef = useRef({
    rowHeight,
    fallbackRowHeight,
    reserve,
    bottomGap,
    minRows,
    maxRows,
    step,
    observe,
  });
  optionsRef.current = {
    rowHeight,
    fallbackRowHeight,
    reserve,
    bottomGap,
    minRows,
    maxRows,
    step,
    observe,
  };

  /** The last few *measured* counts, before any damping. */
  const historyRef = useRef<number[]>([]);

  const measure = useCallback((): void => {
    const container = containerRef.current;
    const options = optionsRef.current;
    if (!container) return;

    const foldY = findFoldY(container);

    const perRow = (options.rowHeight() ?? 0) || options.fallbackRowHeight;
    if (perRow <= 0) return;

    const free =
      foldY -
      container.getBoundingClientRect().top -
      options.bottomGap -
      sum(options.reserve ?? []);

    // Off-screen measures as negative space: no reading yet, not "none fit".
    if (free <= 0) return;

    const raw = Math.floor(free / perRow);
    const stepped = options.step > 1 ? Math.floor(raw / options.step) * options.step : raw;
    const fitted = Math.min(Math.max(stepped, options.minRows), options.maxRows);

    // Break an A -> B -> A oscillation, which the feedback described above can
    // produce. Both neighbours are checked so a steady reading never trips it,
    // and the history holds raw measurements so damping cannot feed itself.
    // Settling on the smaller of the two is the safe direction: too few rows
    // leaves a gap, too many puts the last row under the fold.
    const history = historyRef.current;
    const previous = history[history.length - 1];
    const beforeThat = history[history.length - 2];
    const isOscillating = beforeThat === fitted && previous !== fitted;

    historyRef.current = [...history, fitted].slice(-3);
    setRows((current: number) => {
      const next = isOscillating ? Math.min(fitted, previous) : fitted;
      return current === next ? current : next;
    });

    const usableHeight = Math.max(0, Math.round(free));
    setAvailableHeight((current: number) => (current === usableHeight ? current : usableHeight));
  }, []);

  useLayoutEffect(() => {
    let frame = 0;
    const schedule = (): void => {
      // Coalesce bursts of observer callbacks into one measurement per frame;
      // measuring inside the callback itself risks a ResizeObserver loop. While
      // the tab is hidden frames stop arriving and so does re-measuring, which
      // is what we want — the pending frame lands when it becomes visible.
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };

    // The first pass runs synchronously, before paint, so the list is never
    // shown at a placeholder size and then resized.
    measure();

    const container = containerRef.current;
    const observer = new ResizeObserver(schedule);

    if (container) {
      observer.observe(container);

      findClippingAncestors(container).forEach((ancestor: HTMLElement) => {
        if (ancestor !== container) observer.observe(ancestor);
      });

      optionsRef.current.observe?.().forEach((element: Element | null) => {
        if (element) observer.observe(element);
      });
    } else {
      observer.observe(document.documentElement);
    }

    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    // Scrolling moves the container relative to the fold without resizing it.
    window.addEventListener('scroll', schedule, true);

    return (): void => {
      if (frame) window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
      window.removeEventListener('scroll', schedule, true);
    };
  }, [measure]);

  // Re-measure when a numeric knob changes; the rest is covered by the
  // observers above.
  useEffect(() => {
    measure();
  }, [measure, bottomGap, minRows, maxRows, step, fallbackRowHeight]);

  return { containerRef, rows, availableHeight };
}

export default useAutoFitRows;
